import { Info } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'

import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import Form from '@/elements/Form'
import FormInput from '@/elements/FormInput'
import FormFile from '@/elements/FormFile'
import Button from '@/elements/Button'
import Modal from '@/components/Modal'
import useBreakpoints from '@/hooks/useBreakpoints'
import useOrganizationInfoForm from '@/hooks/forms/useOrganizationInfoForm'
import Loading from '@/elements/Loading'
import { colors } from '@/utils/theme'
import toast from '@/utils/toast'
import type { OrganizationResponse } from '@/api/types/Organization'
import {
  updateOrganization,
  useGetOrganizationDetails,
  createOrganizationExclusionWord,
  deleteOrganizationExclusionWord
} from '@/api/organization'

import ExclusionWords from './ExclusionWords'

function OrganizationInformation() {
  const [showExclusionWordsInfo, setShowExclusionWordsInfo] = useState(false)
  const { isTablet, isMobile } = useBreakpoints()
  const {
    data: organizationDetailsData,
    isLoading: organizationDetailsLoading,
    refetch: refetchOrganizationDetails
  } = useGetOrganizationDetails()
  const organizationInfo: Partial<OrganizationResponse> = useMemo(
    () => organizationDetailsData?.data || {},
    [organizationDetailsData]
  )
  const { form, resetForm } = useOrganizationInfoForm(organizationInfo)

  useEffect(() => {
    resetForm(organizationInfo)
  }, [organizationInfo, resetForm])

  const submitUpdateOrganization = useCallback(async () => {
    const isValid = await form.trigger()
    if (!isValid) return
    const dirtyFields = form.formState.dirtyFields
    const formData = new FormData()
    const values = form.getValues()

    formData.append('name', values.name)
    if (dirtyFields.cover) {
      formData.append('coverAction', values.cover ? 'update' : 'delete')
      if (values.cover) {
        formData.append('cover', values.cover)
      }
    }

    try {
      await updateOrganization(formData)
      refetchOrganizationDetails()
      toast.success('Organization details updated successfully!')
    } catch {
      toast.error('Failed to update organization details')
    }
  }, [form, refetchOrganizationDetails])

  const handleExclusionWords = useCallback((action: 'add' | 'remove') => {
    return async (data: string | { word: string }) => {
      if (action === 'add') {
        await createOrganizationExclusionWord(data as { word: string })
      } else if (action === 'remove') {
        await deleteOrganizationExclusionWord(data as string)
      }
    }
  }, [])

  return (
    <Flex
      wfull
      className='card'
      align='flex-end'
      gap={8}
      p={isMobile ? 3 : isTablet ? 5 : 7}
    >
      <Flex wfull inline={!isTablet} gap={4} wrap>
        <Flex fraction={4} gap={2}>
          <Text variant='h3'>Basic Information</Text>
          <Text color='black-light-40'>
            Update organization name and cover image (optional).
          </Text>
        </Flex>
        <Flex fraction={6} wfull>
          {organizationDetailsLoading ? (
            <Flex align='center' justify='center' wfull pt={8}>
              <Loading />
            </Flex>
          ) : (
            <Form onSubmit={submitUpdateOrganization}>
              <Controller
                name='name'
                control={form.control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    type='text'
                    label='Name'
                    placeholder='Enter organization name'
                    dirty={!!form.formState.dirtyFields.name}
                    error={form.formState.errors.name?.message}
                  />
                )}
              />
              <Controller
                name='cover'
                control={form.control}
                render={({ field }) => (
                  <FormFile
                    {...field}
                    files={field.value ? [field.value] : []}
                    label='Cover Image'
                    placeholder='Upload Photo'
                    accept='.png, .jpg, .jpeg'
                    onChange={e => {
                      const file = e.target.files?.[0]
                      field.onChange(file)
                    }}
                    onClear={() => {
                      form.setValue('cover', null)
                      form.setValue('previousCover', '')
                      field.onChange(null)
                    }}
                    error={form.formState.errors.cover?.message}
                    instructions='Recommended dimensions: 1012x302 pixels. JPEG or PNG files supported.'
                    preview='default'
                    scale='3.35:1'
                    previousImageUrl={form.watch('previousCover') as string}
                  />
                )}
              />
              <Flex className='form-controls'>
                <Button
                  variant='secondary'
                  onClick={() => resetForm(organizationInfo)}
                >
                  Cancel
                </Button>
                <Button
                  variant='accent'
                  submit
                  disabled={!form.formState.isDirty}
                >
                  Save Changes
                </Button>
              </Flex>
            </Form>
          )}
        </Flex>
      </Flex>

      <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

      <Flex wfull inline={!isTablet} gap={4} wrap>
        <Flex fraction={4} gap={2} wfull>
          <Flex inline gap={2}>
            <Text variant='h3'>Exclude Alerts by Context (Optional)</Text>
            <Flex mt={1}>
              <Button variant='textOnly' onClick={() => setShowExclusionWordsInfo(true)}>
                <Info
                  size={24}
                  style={{
                    minWidth: 24,
                    minHeight: 24,
                  }}
                />
              </Button>
              {showExclusionWordsInfo && (
                <Modal
                  title={<Info size={24} color={colors['black']} />}
                  isOpen={showExclusionWordsInfo}
                  onCancel={() => setShowExclusionWordsInfo(false)}
                  style={{
                    width: 642,
                  }}
                >
                  <Flex gap={3}>
                    <Text variant='h4' color='black'>Use this to filter out alerts tied to unrelated industries, sports, or public topics that don't apply to you</Text>
                    <Text variant='textblock'>For example, if you share a name with a prominent real estate agent, you can add keywords like “real estate agent,” “open house,” or “realtor.” Watchdog will suppress alerts where your name or aliases appear in these contexts.</Text>
                  </Flex>
                </Modal>
              )}
            </Flex>
          </Flex>
          <Text color='black-light-40'>
            Add contexts that are not related to the individuals but may appear alongside their name or aliases online. When selected, Watchdog will suppress alerts where the individuals' name or aliases appear in these contexts. This helps reduce alerts caused by people with the same name or by unrelated topics.
          </Text>
        </Flex>
        <Flex fraction={6} wfull>
          <ExclusionWords
            handleCreate={handleExclusionWords('add')}
            handleDelete={handleExclusionWords('remove')}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

export default OrganizationInformation
